
import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { DocumentGroup } from "@/models/DocumentGroup";
import { documentGroupApi } from "@/services/api";
import { useToast } from "@/hooks/use-toast";
import { DocumentGroupList } from "./DocumentGroupList";
import { useAuth } from "@/contexts/AuthContext";

interface DocumentGroupSelectorProps {
  selectedGroups: number[];
  onSelectGroup: (documentGroup: DocumentGroup) => void;
  onRemoveGroup: (groupId: number) => void;
  managementCompanyId?: number;
  associationId?: number;
}

export const DocumentGroupSelector = ({
  selectedGroups,
  onSelectGroup,
  onRemoveGroup,
  managementCompanyId,
  associationId,
}: DocumentGroupSelectorProps) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const { toast } = useToast();
  const { user } = useAuth();
  const { selectedRole } = useAuth();

  // Use provided management company ID or fall back to the one from user's session
  const effectiveManagementCompanyId = managementCompanyId || selectedRole?.entityId;
  
  const { data: documentGroups = [], isLoading } = useQuery({
    queryKey: ["document-groups", { search: searchTerm, management_company_id: selectedRole?.entityId }],
    queryFn: () => documentGroupApi.list({ 
      queryKey: ["document-groups", { 
        search: searchTerm,
        management_company_id: effectiveManagementCompanyId
      }] 
    }),
    meta: {
      onError: (error: Error) => {
        toast({
          title: "Error loading document groups",
          description: error.message,
          variant: "destructive"
        });
      }
    }
  });
  
  // Filter out already selected groups
  const availableGroups = Array.isArray(documentGroups) 
    ? documentGroups.filter(group => !selectedGroups.includes(group.id))
    : [];

  // Find dialog container for nested popover
  const [dialogContainer, setDialogContainer] = useState<HTMLElement | null>(null);
  
  useEffect(() => {
    // Look for the closest dialog container when component mounts
    const container = document.querySelector('[data-popover-container="true"]') || 
                      document.querySelector('[role="dialog"]');
    setDialogContainer(container as HTMLElement);
  }, []);

  return (
    <div className="space-y-3">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="w-full justify-start">
            <Plus className="mr-2 h-4 w-4" />
            Add Document Group
          </Button>
        </PopoverTrigger>
        <PopoverContent 
          className="w-80 p-0" 
          align="start"
          sideOffset={5}
          container={dialogContainer}
        >
          <Command>
            <CommandInput 
              placeholder="Search document groups..." 
              value={searchTerm}
              onValueChange={setSearchTerm}
            />
            <CommandList>
              <CommandEmpty>No document groups found.</CommandEmpty>
              {availableGroups.length > 0 && (
                <CommandGroup>
                  {availableGroups.map((group) => (
                    <CommandItem
                      key={group.id}
                      onSelect={() => {
                        onSelectGroup(group);
                        setOpen(false);
                      }}
                    >
                      <Check
                        className={`mr-2 h-4 w-4 ${
                          selectedGroups.includes(group.id) ? "opacity-100" : "opacity-0"
                        }`}
                      />
                      {group.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      
      <DocumentGroupList 
        selectedGroups={selectedGroups}
        onRemoveGroup={onRemoveGroup}
        managementCompanyId={effectiveManagementCompanyId}
        associationId={associationId}
      />
    </div>
  );
};
